import { useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { getThreadAnswersQuery } from '@ts-core/graphql/forum/queries';
import {
  createThreadAnswerMutation,
  deleteThreadAnswerMutation,
  upvoteThreadAnswerMutation,
  downvoteThreadAnswerMutation,
  reportThreadAnswerMutation,
} from '@ts-core/graphql/forum/mutations';

import { useMutationCompletionHandler, getErrorMessage } from '@ts-core/lib/apollo';

export default function useThreadAnswers({ courseId, threadId, refetchQueries, onThreadAnswerCreated } = {}) {
  const { removeItemFromCache, addItemToCache, onError, onCompleted } = useMutationCompletionHandler();

  const { data, loading } = useQuery(getThreadAnswersQuery, {
    variables: { courseId, threadId },
    skip: !courseId || !threadId,
    fetchPolicy: 'cache-and-network',
  });

  const threadAnswers = useMemo(() => data?.threadAnswers, [data]);

  /**
   * Create Thread Answer
   */
  const [createThreadAnswer, { loading: creatingThreadAnswer, error: createThreadAnswerError }] = useMutation(createThreadAnswerMutation, {
    onError,
    onCompleted: onThreadAnswerCreated,
    update: addItemToCache({
      query: getThreadAnswersQuery,
      queryField: 'threadAnswers',
      variables: {
        courseId,
        threadId,
      },
      mutationField: 'createThreadAnswer',
    }),
  });
  const onCreateThreadAnswer = useCallback(
    (input) => {
      createThreadAnswer({
        variables: {
          courseId,
          threadId,
          input,
        },
      });
    },
    [createThreadAnswer, courseId, threadId]
  );

  /**
   * Delete Thread Answer
   */
  const [deleteThreadAnswer] = useMutation(deleteThreadAnswerMutation, {
    onError,
    update: removeItemFromCache({
      query: getThreadAnswersQuery,
      queryField: 'threadAnswers',
      variables: { courseId, threadId },
      mutationField: 'deleteThreadAnswer',
    }),
    refetchQueries,
  });
  const onDeleteAnswer = useCallback(
    (answer) => {
      deleteThreadAnswer({
        variables: {
          courseId,
          threadId,
          answerId: answer._id,
        },
      });
    },
    [deleteThreadAnswer, courseId, threadId]
  );

  /**
   * Upvote Thread Answer
   */
  const [upvoteThreadAnswer] = useMutation(upvoteThreadAnswerMutation, {
    onError,
  });
  const onUpvoteThreadAnswer = useCallback(
    (answer) => {
      const updatedAnswer = {
        ...answer,
        score: answer.score + 1,
        canUpvote: false,
        canDownvote: false,
      };
      upvoteThreadAnswer({
        variables: {
          courseId,
          threadId,
          answerId: answer._id,
        },
        optimisticResponse: {
          upvoteThreadAnswer: updatedAnswer,
        },
      });
    },
    [upvoteThreadAnswer, courseId, threadId]
  );

  /**
   * Upvote Thread Answer
   */
  const [downvoteThreadAnswer] = useMutation(downvoteThreadAnswerMutation, {
    onError,
  });
  const onDownvoteThreadAnswer = useCallback(
    (answer) => {
      const updatedAnswer = {
        ...answer,
        score: answer.score - 1,
        canUpvote: false,
        canDownvote: false,
      };
      downvoteThreadAnswer({
        variables: {
          courseId,
          threadId,
          answerId: answer._id,
        },
        optimisticResponse: {
          upvoteThreadAnswer: updatedAnswer,
        },
      });
    },
    [downvoteThreadAnswer, courseId, threadId]
  );

  /**
   * Report Thread
   */
  const [reportThreadAnswer] = useMutation(reportThreadAnswerMutation, {
    onError,
    onCompleted,
  });
  const onReportThreadAnswer = useCallback(
    (answer, input) => {
      reportThreadAnswer({
        variables: {
          courseId,
          threadId,
          answerId: answer._id,
          input,
        },
      });
    },
    [reportThreadAnswer, courseId, threadId]
  );

  return {
    threadAnswers,
    loading,

    onCreateThreadAnswer,
    creatingThreadAnswer,
    createThreadAnswerError: getErrorMessage({
      error: createThreadAnswerError,
    }),

    onDeleteAnswer,

    onUpvoteThreadAnswer,

    onDownvoteThreadAnswer,

    onReportThreadAnswer,
  };
}
